// Date utility functions that respect user preferences

// Get user's preferred date format from localStorage or use default
const getUserDateFormat = () => {
  return localStorage.getItem('dateFormat') || 'MM/DD/YYYY';
};

// Format date to display format respecting user preferences
export const formatDate = (date, format = 'display') => {
  if (!date) return '';
  
  const dateObj = new Date(date);
  if (isNaN(dateObj.getTime())) return '';
  
  const userDateFormat = getUserDateFormat();
  
  // If format is 'display', use user's preferred format
  if (format === 'display') {
    const day = dateObj.getDate().toString().padStart(2, '0');
    const month = (dateObj.getMonth() + 1).toString().padStart(2, '0');
    const year = dateObj.getFullYear();
    
    switch (userDateFormat) {
      case 'DD/MM/YYYY':
        return `${day}/${month}/${year}`;
      case 'YYYY-MM-DD':
        return `${year}-${month}-${day}`;
      case 'MM/DD/YYYY':
      default:
        return `${month}/${day}/${year}`;
    }
  }
  
  const formats = {
    input: 'yyyy-MM-dd',
    short: 'MM/dd/yyyy',
    long: 'MMMM dd, yyyy',
    time: 'MMM dd, yyyy HH:mm',
    timeShort: 'MM/dd/yyyy HH:mm',
    iso: 'yyyy-MM-dd\'T\'HH:mm:ss.SSSxxx',
  };
  
  const selectedFormat = formats[format] || formats.short;
  
  // Simple formatting for common patterns
  if (selectedFormat === 'yyyy-MM-dd') {
    return dateObj.toISOString().split('T')[0];
  }
  
  if (selectedFormat === 'MM/dd/yyyy') {
    return dateObj.toLocaleDateString('en-US');
  }
  
  if (selectedFormat === 'MMMM dd, yyyy') {
    return dateObj.toLocaleDateString('en-US', {
      month: 'long',
      day: '2-digit',
      year: 'numeric'
    });
  }
  
  if (selectedFormat === 'MMM dd, yyyy HH:mm') {
    return dateObj.toLocaleDateString('en-US', {
      month: 'short',
      day: '2-digit',
      year: 'numeric'
    }) + ' ' + dateObj.toLocaleTimeString('en-US', {
      hour: '2-digit',
      minute: '2-digit'
    });
  }
  
  if (selectedFormat === 'MM/dd/yyyy HH:mm') {
    return dateObj.toLocaleDateString('en-US') + ' ' + dateObj.toLocaleTimeString('en-US', {
      hour: '2-digit',
      minute: '2-digit'
    });
  }
  
  return dateObj.toLocaleDateString('en-US');
};

// Set user's date format preference
export const setDateFormat = (format) => {
  localStorage.setItem('dateFormat', format);
};

// Get user's date format preference
export const getDateFormat = () => {
  return localStorage.getItem('dateFormat') || 'MM/DD/YYYY';
};

// Get date range for predefined periods
export const getDateRange = (period) => {
  const now = new Date();
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  
  switch (period) {
    case 'today':
      return {
        start: today,
        end: new Date(today.getTime() + 24 * 60 * 60 * 1000 - 1)
      };
      
    case 'yesterday':
      return {
        start: new Date(today.getTime() - 24 * 60 * 60 * 1000),
        end: new Date(today.getTime() - 1)
      };
      
    case 'this_week':
      const startOfWeek = new Date(today);
      startOfWeek.setDate(today.getDate() - today.getDay());
      return {
        start: startOfWeek,
        end: new Date(startOfWeek.getTime() + 7 * 24 * 60 * 60 * 1000 - 1)
      };
      
    case 'last_week':
      const lastWeekStart = new Date(today);
      lastWeekStart.setDate(today.getDate() - today.getDay() - 7);
      return {
        start: lastWeekStart,
        end: new Date(lastWeekStart.getTime() + 7 * 24 * 60 * 60 * 1000 - 1)
      };
      
    case 'this_month':
      return {
        start: new Date(now.getFullYear(), now.getMonth(), 1),
        end: new Date(now.getFullYear(), now.getMonth() + 1, 0, 23, 59, 59, 999)
      };
      
    case 'last_month':
      return {
        start: new Date(now.getFullYear(), now.getMonth() - 1, 1),
        end: new Date(now.getFullYear(), now.getMonth(), 0, 23, 59, 59, 999)
      };
      
    case 'this_quarter':
      const quarter = Math.floor(now.getMonth() / 3);
      const quarterStart = new Date(now.getFullYear(), quarter * 3, 1);
      return {
        start: quarterStart,
        end: new Date(now.getFullYear(), (quarter + 1) * 3, 0, 23, 59, 59, 999)
      };
      
    case 'last_quarter':
      const lastQuarter = Math.floor(now.getMonth() / 3) - 1;
      const lastQuarterStart = new Date(now.getFullYear(), lastQuarter * 3, 1);
      return {
        start: lastQuarterStart,
        end: new Date(now.getFullYear(), (lastQuarter + 1) * 3, 0, 23, 59, 59, 999)
      };
      
    case 'this_year':
      return {
        start: new Date(now.getFullYear(), 0, 1),
        end: new Date(now.getFullYear(), 11, 31, 23, 59, 59, 999)
      };
      
    case 'last_year':
      return {
        start: new Date(now.getFullYear() - 1, 0, 1),
        end: new Date(now.getFullYear() - 1, 11, 31, 23, 59, 59, 999)
      };
      
    default:
      return { start: today, end: today };
  }
};

// Calculate days between two dates
export const daysBetween = (date1, date2) => {
  const oneDay = 24 * 60 * 60 * 1000;
  const first = new Date(date1);
  const second = new Date(date2);
  return Math.round(Math.abs((first - second) / oneDay));
};

// Check if date is overdue
export const isOverdue = (dueDate) => {
  if (!dueDate) return false;
  const today = new Date();
  const due = new Date(dueDate);
  return due < today;
};

// Get relative time string
export const getRelativeTime = (date) => {
  if (!date) return '';
  
  const now = new Date();
  const past = new Date(date);
  const diffInSeconds = Math.floor((now - past) / 1000);
  
  if (diffInSeconds < 60) return 'just now';
  if (diffInSeconds < 3600) return `${Math.floor(diffInSeconds / 60)} minutes ago`;
  if (diffInSeconds < 86400) return `${Math.floor(diffInSeconds / 3600)} hours ago`;
  if (diffInSeconds < 2592000) return `${Math.floor(diffInSeconds / 86400)} days ago`;
  if (diffInSeconds < 31536000) return `${Math.floor(diffInSeconds / 2592000)} months ago`;
  return `${Math.floor(diffInSeconds / 31536000)} years ago`;
};

// Get month name
export const getMonthName = (month, format = 'short') => {
  const months = [
    'January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December'
  ];
  
  const monthIndex = typeof month === 'number' ? month : parseInt(month) - 1;
  const monthName = months[monthIndex];
  
  return format === 'short' ? monthName.substring(0, 3) : monthName;
};

// Get quarter from month
export const getQuarter = (month) => {
  const monthNum = typeof month === 'number' ? month : parseInt(month);
  return Math.ceil(monthNum / 3);
};

// Validate date string
export const isValidDate = (dateString) => {
  const date = new Date(dateString);
  return date instanceof Date && !isNaN(date);
};
